// Ambientes (como os teamspaces do Notion): criar, renomear, trocar o ícone,
// arquivar e gerenciar quem é membro e com qual papel.

import * as store from "./store.js";
import * as router from "./router.js";
import { api } from "./api.js";
import { el, dialog, toast, confirmDialog } from "./dom.js";
import { iconNode, ui } from "./icons.js";
import { iconPicker } from "./picker.js";
import { avatar } from "./people.js";

export const ROLES = [
  ["view", "Pode ver", "Lê as páginas e comenta"],
  ["edit", "Pode editar", "Cria e edita páginas e tarefas"],
  ["admin", "Administra", "Também gerencia membros e o ambiente"],
];

export function createWorkspaceDialog() {
  let icon = "lucide:layers:blue";
  const iconBtn = el("button", { type: "button", class: "ws-icon-btn", title: "Trocar ícone" }, iconNode(icon));
  iconBtn.addEventListener("click", () => iconPicker(iconBtn, (v) => { icon = v || "🗂️"; iconBtn.replaceChildren(iconNode(icon)); }, { current: icon }));
  const name = el("input", { placeholder: "Ex.: Dev SS, Paciente Control, Financeiro", required: true, maxlength: 100 });
  const desc = el("textarea", { rows: 2, placeholder: "Para que serve este ambiente (opcional)" });
  const others = (store.state.users || []).filter((u) => u.id !== store.state.me.id);
  const picks = new Map();
  const people = el("div", { class: "people-pick" }, others.map((u) => {
    const sel = el("select", { class: "small" }, el("option", { value: "" }, "Sem acesso"), ROLES.map(([v, l]) => el("option", { value: v }, l)));
    sel.addEventListener("change", () => (sel.value ? picks.set(u.id, sel.value) : picks.delete(u.id)));
    return el("div", { class: "person-row" }, avatar(u, "sm"), el("span", { class: "grow" }, u.name), sel);
  }));
  dialog((close) => [
    el("h3", {}, "Novo ambiente"),
    el("p", { class: "muted small" }, "Um ambiente agrupa páginas e quadros de um time ou projeto. Só os membros veem o que está dentro."),
    el("div", { class: "ws-name-row" }, iconBtn, name),
    desc,
    others.length ? el("h4", {}, "Quem participa") : null,
    others.length ? people : null,
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Cancelar"),
      el("button", { class: "primary", onclick: async () => {
        if (!name.value.trim()) return name.focus();
        try {
          const { workspace } = await api("POST", "/workspaces", {
            name: name.value.trim(), icon, description: desc.value.trim(),
            members: [...picks].map(([user_id, role]) => ({ user_id, role })),
          });
          await store.loadTree();
          close();
          toast(`Ambiente "${workspace.name}" criado.`);
        } catch (err) { toast(err.message); }
      } }, "Criar ambiente")),
  ]);
  requestAnimationFrame(() => name.focus());
}

export async function workspaceDialog(wsId, tab = "members") {
  const body = el("div", { class: "ws-body" }, el("p", { class: "muted" }, "Carregando…"));
  const tabs = el("div", { class: "picker-tabs" });
  let data = null;
  const dlg = dialog((close) => [el("h3", { class: "ws-title" }), tabs, body, el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Fechar"))], { wide: true });
  const load = async () => {
    data = await api("GET", `/workspaces/${wsId}`);
    draw();
  };
  const isAdmin = () => data.workspace.role === "admin";
  const draw = () => {
    const w = data.workspace;
    dlg.querySelector(".ws-title").replaceChildren(iconNode(w.icon, "🗂️"), " ", w.name);
    tabs.replaceChildren(...[["members", `Membros (${data.members.length})`], isAdmin() && ["general", "Geral"]].filter(Boolean).map(([id, l]) =>
      el("button", { class: "picker-tab" + (tab === id ? " on" : ""), onclick: () => { tab = id; draw(); } }, l)));
    if (tab === "general" && isAdmin()) drawGeneral(w);
    else drawMembers();
  };
  const drawMembers = () => {
    const memberIds = new Set(data.members.map((m) => m.id));
    const candidates = (store.state.users || []).filter((u) => !memberIds.has(u.id));
    const who = el("select", {}, candidates.map((u) => el("option", { value: u.id }, u.name)));
    const role = el("select", {}, ROLES.map(([v, l]) => el("option", { value: v, selected: v === "edit" }, l)));
    body.replaceChildren(
      isAdmin() && candidates.length ? el("div", { class: "ws-add" }, ui("userPlus"), who, role,
        el("button", { class: "primary small", onclick: async () => {
          try {
            data.members = (await api("POST", `/workspaces/${wsId}/members`, { user_id: who.value, role: role.value })).members;
            draw();
          } catch (err) { toast(err.message); }
        } }, "Adicionar")) : null,
      el("div", { class: "ws-members" }, data.members.map((m) => el("div", { class: "person-row" },
        avatar(m, "sm"),
        el("div", { class: "grow" }, el("strong", {}, m.name, m.id === store.state.me.id ? " (você)" : ""), el("div", { class: "muted small" }, [m.title, m.email].filter(Boolean).join(" · "))),
        isAdmin()
          ? el("select", { class: "small", onchange: async (e) => {
            try {
              data.members = (await api("PATCH", `/workspaces/${wsId}/members/${m.id}`, { role: e.target.value })).members;
            } catch (err) { toast(err.message); }
            draw();
          } }, ROLES.map(([v, l]) => el("option", { value: v, selected: v === m.role }, l)))
          : el("span", { class: "muted small" }, ROLES.find((r) => r[0] === m.role)?.[1] || m.role),
        isAdmin() || m.id === store.state.me.id ? el("button", { class: "icon-btn tiny", title: m.id === store.state.me.id ? "Sair do ambiente" : "Remover do ambiente", onclick: async () => {
          const self = m.id === store.state.me.id;
          if (!(await confirmDialog(self ? `Sair do ambiente "${data.workspace.name}"? Você deixa de ver as páginas dele.` : `Remover ${m.name} do ambiente? A pessoa deixa de ver as páginas dele.`, { ok: self ? "Sair" : "Remover", danger: true }))) return;
          try {
            data.members = (await api("DELETE", `/workspaces/${wsId}/members/${m.id}`)).members;
            if (self) { dlg.close(); await store.loadTree(); router.go({ view: "home" }); return; }
            draw();
          } catch (err) { toast(err.message); }
        } }, ui("x", 14)) : null))),
      el("div", { class: "role-help muted small" }, ROLES.map(([, l, h]) => el("div", {}, el("strong", {}, l), `: ${h}.`))));
  };
  const drawGeneral = (w) => {
    let icon = w.icon;
    const iconBtn = el("button", { type: "button", class: "ws-icon-btn" }, iconNode(icon, "🗂️"));
    iconBtn.addEventListener("click", () => iconPicker(iconBtn, (v) => { icon = v; iconBtn.replaceChildren(iconNode(icon, "🗂️")); }, { current: icon }));
    const name = el("input", { value: w.name, maxlength: 100 });
    const desc = el("textarea", { rows: 3, value: w.description || "" });
    body.replaceChildren(
      el("div", { class: "ws-name-row" }, iconBtn, name),
      el("label", { class: "field" }, "Descrição", desc),
      el("div", { class: "row-actions" },
        el("button", { class: "primary", onclick: async () => {
          try {
            data.workspace = { ...data.workspace, ...(await api("PATCH", `/workspaces/${wsId}`, { name: name.value.trim(), icon, description: desc.value })).workspace };
            await store.reloadWorkspaces();
            toast("Ambiente salvo.");
            draw();
          } catch (err) { toast(err.message); }
        } }, "Salvar"),
        el("span", { class: "grow" }),
        el("button", { class: "danger-btn", onclick: async () => {
          if (!(await confirmDialog(`Arquivar o ambiente "${w.name}"? Ele some da barra lateral de todo mundo. O administrador do Ordo pode restaurar depois.`, { ok: "Arquivar", danger: true }))) return;
          try {
            await api("PATCH", `/workspaces/${wsId}`, { archived: true });
            dlg.close();
            await store.loadTree();
            router.go({ view: "home" });
          } catch (err) { toast(err.message); }
        } }, "Arquivar ambiente")));
  };
  try {
    await load();
  } catch (err) {
    body.replaceChildren(el("p", { class: "error" }, err.message));
  }
}
